import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { CheckCircle2, Loader2, X } from 'lucide-react';
import {
  AVALIACAO_GERAL_JANELA_DIAS,
  AVALIACAO_JANELA_DIAS,
  AVALIACAO_POS_ACAO_INTERVALO_DIAS,
  faixaNotaAvaliacao,
  type AvaliacaoEvento,
} from '@patrociconnect/shared';
import { api, getErrorMessage } from '@/lib/api';
import { APP_VERSION } from '@/lib/errorReporting';
import { onMicroSurvey, PENDING_SURVEY_KEY } from '@/lib/feedbackBus';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

const DAY_MS = 24 * 60 * 60 * 1000;
/** Pesquisa pós-ação: no máximo uma a cada 7 dias por usuário */
const MIN_INTERVAL_MS = AVALIACAO_POS_ACAO_INTERVALO_DIAS * DAY_MS;
/** O mesmo evento não é perguntado de novo em 30 dias */
const SAME_EVENT_INTERVAL_MS = AVALIACAO_JANELA_DIAS * DAY_MS;
/** Pesquisa geral (NPS): no máximo a cada 90 dias */
const GENERAL_INTERVAL_MS = AVALIACAO_GERAL_JANELA_DIAS * DAY_MS;
/** Espera o feedback de sucesso da própria ação antes de perguntar */
const SHOW_DELAY_MS = 1500;
const THANKS_DURATION_MS = 3500;
const MAX_COMMENT = 500;

const QUESTIONS: Record<AvaliacaoEvento, string> = {
  ativo_publicado: 'Como foi publicar seu primeiro ativo?',
  proposta_enviada: 'Como foi enviar sua primeira proposta?',
  candidatura_enviada: 'Como foi se candidatar a uma chamada de patrocínio?',
  proposta_respondida: 'Foi fácil responder à proposta?',
  acordo_registrado: 'Como foi fechar o acordo?',
  kyc_enviado: 'Como foi enviar sua verificação?',
  cadastro_concluido: 'Como foi criar sua conta?',
  geral: 'Quanto você recomendaria a PatrociConnect?',
};

const NPS_RATINGS = Array.from({ length: 11 }, (_, value) => ({
  value,
  label: value <= 6 ? 'Pouco provável' : value <= 8 ? 'Talvez' : 'Muito provável',
}));

const RATINGS = [
  { value: 1, label: 'Muito difícil' },
  { value: 2, label: 'Difícil' },
  { value: 3, label: 'Razoável' },
  { value: 4, label: 'Fácil' },
  { value: 5, label: 'Muito fácil' },
];

interface SurveyHistory {
  /** ISO 8601 da última pesquisa pós-ação exibida */
  last?: string;
  eventos: Partial<Record<AvaliacaoEvento, string>>;
}

function storageKey(uid: string): string {
  return `pc:microSurvey:${uid}`;
}

function readHistory(uid: string): SurveyHistory {
  try {
    const raw = localStorage.getItem(storageKey(uid));
    if (!raw) return { eventos: {} };
    const parsed = JSON.parse(raw) as SurveyHistory;
    return { last: parsed.last, eventos: parsed.eventos ?? {} };
  } catch {
    return { eventos: {} };
  }
}

function writeHistory(uid: string, history: SurveyHistory): void {
  try {
    localStorage.setItem(storageKey(uid), JSON.stringify(history));
  } catch {
    // Sem localStorage o backend ainda ignora duplicatas por usuário/evento
  }
}

function isRecent(iso: string | undefined, windowMs: number): boolean {
  if (!iso) return false;
  const time = Date.parse(iso);
  return !Number.isNaN(time) && Date.now() - time < windowMs;
}

function canAsk(uid: string, evento: AvaliacaoEvento): boolean {
  const history = readHistory(uid);
  if (evento === 'geral') return !isRecent(history.eventos.geral, GENERAL_INTERVAL_MS);
  return !isRecent(history.last, MIN_INTERVAL_MS) && !isRecent(history.eventos[evento], SAME_EVENT_INTERVAL_MS);
}

function markAsked(uid: string, evento: AvaliacaoEvento): void {
  const history = readHistory(uid);
  const now = new Date().toISOString();
  writeHistory(uid, {
    // A pesquisa geral tem janela própria e não consome a cota semanal pós-ação
    last: evento === 'geral' ? history.last : now,
    eventos: { ...history.eventos, [evento]: now },
  });
}

/** Regras do backend (primeiro ativo/proposta, janelas, idade da conta) */
async function isEligible(evento: AvaliacaoEvento): Promise<boolean> {
  try {
    const result = await api.get<{ elegivel: boolean }>(`/feedback/pesquisa/elegibilidade?evento=${evento}`);
    return result.elegivel;
  } catch (eligibilityError) {
    // Na dúvida, não pergunta: a pesquisa é opcional e não deve incomodar
    console.error('Falha ao verificar elegibilidade da micro-pesquisa', { evento, eligibilityError });
    return false;
  }
}

/**
 * Após uma ação importante: tenta a pesquisa pós-ação; se não couber (cota
 * semanal, primeiro-só, etc.), tenta a pesquisa geral (NPS, a cada 90 dias).
 */
async function pickSurvey(uid: string, requested: AvaliacaoEvento): Promise<AvaliacaoEvento | null> {
  if (requested !== 'geral' && canAsk(uid, requested) && (await isEligible(requested))) return requested;
  if (canAsk(uid, 'geral') && (await isEligible('geral'))) return 'geral';
  return null;
}

function takePending(): AvaliacaoEvento | null {
  try {
    const value = sessionStorage.getItem(PENDING_SURVEY_KEY) as AvaliacaoEvento | null;
    sessionStorage.removeItem(PENDING_SURVEY_KEY);
    return value && value in QUESTIONS ? value : null;
  } catch {
    return null;
  }
}

/**
 * Micro-pesquisa não modal após momentos-chave. Montada uma vez pelo
 * FeedbackButton; exibe um cartão discreto no canto inferior esquerdo.
 */
export function MicroSurveyHost() {
  const baseId = useId();
  const uid = useAuthStore((state) => state.user?.uid ?? null);
  const [evento, setEvento] = useState<AvaliacaoEvento | null>(null);
  const [nota, setNota] = useState<number | null>(null);
  const [comentario, setComentario] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isThanked, setIsThanked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const showTimer = useRef<number | undefined>(undefined);

  const schedule = useCallback(
    (requested: AvaliacaoEvento) => {
      if (!uid) return;
      // Consumido aqui: evita reaparecer após recarregar a página
      takePending();
      window.clearTimeout(showTimer.current);
      showTimer.current = window.setTimeout(() => {
        void pickSurvey(uid, requested).then((chosen) => {
          if (!chosen) return;
          markAsked(uid, chosen);
          setNota(null);
          setComentario('');
          setError(null);
          setIsThanked(false);
          setEvento(chosen);
        });
      }, SHOW_DELAY_MS);
    },
    [uid]
  );

  // Pedido feito antes de uma troca de layout (ex.: cadastro → dashboard)
  useEffect(() => {
    if (!uid) return;
    let pending: AvaliacaoEvento | null = null;
    try {
      pending = sessionStorage.getItem(PENDING_SURVEY_KEY) as AvaliacaoEvento | null;
    } catch {
      pending = null;
    }
    if (pending && pending in QUESTIONS) schedule(pending);
  }, [uid, schedule]);

  useEffect(() => onMicroSurvey(schedule), [schedule]);

  useEffect(() => () => window.clearTimeout(showTimer.current), []);

  useEffect(() => {
    if (!isThanked) return;
    const timer = window.setTimeout(() => setEvento(null), THANKS_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [isThanked]);

  const dismiss = () => setEvento(null);
  const isNps = evento === 'geral';
  const ratings = isNps ? NPS_RATINGS : RATINGS;
  const scale = evento ? faixaNotaAvaliacao(evento) : { min: 1, max: 5 };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!evento || nota === null) return;
    setIsSending(true);
    setError(null);
    try {
      await api.post('/feedback', {
        tipo: 'avaliacao',
        evento,
        nota,
        texto: comentario.trim(),
        tela: window.location.pathname,
        versao: APP_VERSION,
      });
      setIsThanked(true);
    } catch (submitError) {
      setError(getErrorMessage(submitError, 'Não foi possível enviar agora.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div aria-live="polite" className="pointer-events-none fixed bottom-4 left-4 right-4 z-[90] sm:left-6 sm:right-auto sm:w-[22rem]">
      {evento && (
        <section
          aria-labelledby={`${baseId}-titulo`}
          className="pointer-events-auto relative overflow-hidden rounded-2xl border border-line bg-elevated p-4 shadow-elevation-4 animate-slide-up"
        >
          <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-brand-400 to-brand-600" aria-hidden="true" />
          <button
            type="button"
            onClick={dismiss}
            className="absolute right-2 top-2 rounded-md p-1 text-muted transition-colors hover:bg-ink/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            aria-label="Agora não — fechar pesquisa"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>

          {isThanked ? (
            <p className="flex items-center gap-2 pl-2 pr-6 text-sm font-medium text-ink">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-hidden="true" />
              Obrigado! Sua resposta ajuda a melhorar a plataforma.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="pl-2" noValidate>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-300">
                Pergunta rápida · opcional
              </p>
              <h2 id={`${baseId}-titulo`} className="mt-1 pr-6 text-sm font-semibold text-ink">
                {QUESTIONS[evento]}
              </h2>

              <div
                role="group"
                aria-label={isNps ? 'Nota de 0 (nada provável) a 10 (muito provável)' : 'Nota de 1 (muito difícil) a 5 (muito fácil)'}
                className={cn('mt-3 grid', isNps ? 'grid-cols-11 gap-0.5' : 'grid-cols-5 gap-1.5')}
              >
                {ratings.map((rating) => (
                  <button
                    key={rating.value}
                    type="button"
                    aria-pressed={nota === rating.value}
                    aria-label={`${rating.value} — ${rating.label}`}
                    title={rating.label}
                    onClick={() => setNota(rating.value)}
                    className={cn(
                      isNps ? 'h-8 rounded-md text-xs' : 'h-9 rounded-lg text-sm',
                      'border font-semibold tabular-nums transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                      nota === rating.value
                        ? 'border-brand-500 bg-brand-500 text-on-brand'
                        : nota !== null && rating.value < nota
                          ? 'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300'
                          : 'border-line text-ink hover:border-brand-400'
                    )}
                  >
                    {rating.value}
                  </button>
                ))}
              </div>
              <div className="mt-1 flex justify-between text-[11px] text-muted" aria-hidden="true">
                <span>{isNps ? `${scale.min} · Nada provável` : 'Muito difícil'}</span>
                <span>{isNps ? `Muito provável · ${scale.max}` : 'Muito fácil'}</span>
              </div>

              {nota !== null && (
                <div className="mt-3 space-y-2">
                  <label htmlFor={`${baseId}-comentario`} className="sr-only">
                    Comentário opcional
                  </label>
                  <textarea
                    id={`${baseId}-comentario`}
                    className="input min-h-[64px] resize-none text-sm"
                    maxLength={MAX_COMMENT}
                    placeholder={isNps ? 'O que mais pesou na sua nota? (opcional)' : 'Quer contar o motivo? (opcional)'}
                    value={comentario}
                    onChange={(event) => setComentario(event.target.value)}
                  />
                  {error && (
                    <p className="text-xs text-danger" role="alert">
                      {error}
                    </p>
                  )}
                  <div className="flex justify-end gap-2">
                    <button type="button" className="btn-ghost btn-sm" onClick={dismiss} disabled={isSending}>
                      Agora não
                    </button>
                    <button type="submit" className="btn-primary btn-sm" disabled={isSending}>
                      {isSending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                      Enviar
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}
        </section>
      )}
    </div>
  );
}
